import type { GeoJsonFeatureCollection, MapCloudPoint, MapMarker } from './map.js'
import type { LatLng } from './places.js'

export type MapInteractionTrigger = 'click' | 'hover'

export type MapHitTarget =
  | { type: 'marker'; layerId: string; marker: MapMarker; position: LatLng }
  | {
      type: 'feature'
      layerId: string
      feature: GeoJsonFeatureCollection['features'][number]
      position: LatLng
    }
  | {
      type: 'cluster'
      layerId: string
      position: LatLng
      cluster: { pointCount: number; childMarkerIds?: string[] }
    }
  | {
      type: 'pointCloud'
      layerId: string
      position: LatLng
      point: MapCloudPoint
      pointIndex: number
    }

/** Unified hit event for markers, GeoJSON features, and clusters. */
export type MapHitEvent = {
  trigger: MapInteractionTrigger
  target: MapHitTarget
  latlng: LatLng
  nativeEvent?: unknown
}

export type MapHtmlContent =
  | { format: 'text'; value: string }
  | { format: 'html'; value: string }

export type MapSurfaceAnchor = {
  offset?: [number, number]
  maxWidth?: string
}

export type MapPopupSurface = {
  presentation: 'popup'
  content: MapHtmlContent
  anchor?: MapSurfaceAnchor
  dismiss?: { closeButton?: boolean; closeOnMapClick?: boolean }
}

export type MapTooltipSurface = {
  presentation: 'tooltip'
  content: MapHtmlContent
  anchor?: MapSurfaceAnchor
}

export type MapSurface = MapPopupSurface | MapTooltipSurface

export type MapHoverTiming = {
  openDelayMs?: number
  closeDelayMs?: number
}

export type MapClusterInteractionPolicy = {
  /** expand: zoom into cluster. interact: only onHit/surface (no auto zoom). */
  onClick?: 'expand' | 'interact'
}

export type MapInteractionsConfig = {
  /** Side effects: navigation, Vue overlay state, highlight. Runs before surface. */
  onHit: (event: MapHitEvent) => void | Promise<void>

  /** Debounced pointer leave after hover; use to clear Vue overlays (see useMapInteractions). */
  onHoverEnd?: () => void

  /**
   * Map-attached UI (native popup/tooltip). Return null for no map UI.
   * Async supported; stale hover results are discarded by the provider controller.
   */
  surface?: (event: MapHitEvent) => MapSurface | null | Promise<MapSurface | null>

  triggers?: MapInteractionTrigger[]
  hover?: MapHoverTiming
  cluster?: MapClusterInteractionPolicy
}

export const MAP_INTERACTION_DEFAULTS = {
  triggers: ['click', 'hover'] as MapInteractionTrigger[],
  hoverOpenDelayMs: 200,
  hoverCloseDelayMs: 150,
  asyncLoadingThresholdMs: 300,
  clusterOnClick: 'expand' as const,
} as const

export function isMarkerHit(
  event: MapHitEvent,
): event is MapHitEvent & { target: Extract<MapHitTarget, { type: 'marker' }> } {
  return event.target.type === 'marker'
}

export function isFeatureHit(event: MapHitEvent): boolean {
  return event.target.type === 'feature'
}

export function isClusterHit(event: MapHitEvent): boolean {
  return event.target.type === 'cluster'
}

export function isPointCloudHit(
  event: MapHitEvent,
): event is MapHitEvent & { target: Extract<MapHitTarget, { type: 'pointCloud' }> } {
  return event.target.type === 'pointCloud'
}

/** Feature/marker properties excluding `properties.map` (provider styling only). */
export function hitPayload(event: MapHitEvent): Record<string, unknown> {
  if (event.target.type === 'marker') {
    return {}
  }
  if (event.target.type === 'cluster') {
    return {}
  }
  if (event.target.type === 'pointCloud') {
    return { ...(event.target.point.payload ?? {}) }
  }
  const props = event.target.feature.properties
  if (!props || typeof props !== 'object' || Array.isArray(props)) {
    return {}
  }
  const { map: _map, ...rest } = props as Record<string, unknown> & { map?: unknown }
  return { ...rest }
}

export function textSurface(
  value: string,
  opts?: { presentation?: 'popup' | 'tooltip'; anchor?: MapSurfaceAnchor },
): MapSurface {
  const presentation = opts?.presentation ?? 'popup'
  const content: MapHtmlContent = { format: 'text', value }
  if (presentation === 'tooltip') {
    return { presentation: 'tooltip', content, anchor: opts?.anchor }
  }
  return { presentation: 'popup', content, anchor: opts?.anchor }
}

export function htmlSurface(
  value: string,
  opts?: {
    presentation?: 'popup' | 'tooltip'
    anchor?: MapSurfaceAnchor
    dismiss?: MapPopupSurface['dismiss']
  },
): MapSurface {
  const presentation = opts?.presentation ?? 'popup'
  const content: MapHtmlContent = { format: 'html', value }
  if (presentation === 'tooltip') {
    return { presentation: 'tooltip', content, anchor: opts?.anchor }
  }
  return {
    presentation: 'popup',
    content,
    anchor: opts?.anchor,
    dismiss: opts?.dismiss,
  }
}

export const mapSurfaceLoading: MapPopupSurface = {
  presentation: 'popup',
  content: {
    format: 'html',
    value:
      '<div style="font-family:system-ui,sans-serif;font-size:12px;color:#6b7280;padding:4px 0;">Loading…</div>',
  },
  dismiss: { closeButton: false, closeOnMapClick: true },
}
